import type React from 'react';
import { useEffect, useState, useMemo, createContext, type JSX, useCallback } from 'react';

interface NoticeProps {
  children: JSX.Element;
}

interface NoticeContextProps {
  notice?: {
    type?: 'success' | 'warning' | 'error';
    message?: string;
  };
  setNotification?: (type: string, message: string) => void;
  hideNotification?: () => void;
  noticeVisible?: boolean;
  setNoticeVisible?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NoticeContext = createContext<NoticeContextProps>({});

const NoticeProvider: React.FC<NoticeProps> = ({ children }) => {
  const [notice, setNotice] = useState({});
  const [noticeVisible, setNoticeVisible] = useState(false);
  const [notifType, setNotifType] = useState('');

  const setNotification = useCallback((type: string, message: string) => {
    setNoticeVisible((prev) => {
      if (!prev) {
        setNotice({ message, type });
        setNotifType(type);
        return true;
      }
      return prev;
    });
  }, []);

  const hideNotification = () => {
    setNotice({});
    setNoticeVisible(false);
  };

  useEffect(() => {
    if (noticeVisible && notifType !== 'error') {
      const timer = setTimeout(() => hideNotification(), 5000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [noticeVisible]);

  const memoNoticeValue = useMemo(
    () => ({
      hideNotification,
      notice,
      noticeVisible,
      setNoticeVisible,
      setNotification,
    }),
    [notice, noticeVisible],
  );

  return <NoticeContext.Provider value={memoNoticeValue}>{children}</NoticeContext.Provider>;
};

export default NoticeProvider;
